/**
 * The date window both supplier boards read through — the Supplier Orders
 * board and the supplier's own board (both scopes).
 *
 * A supplier order is on a board only when it was CREATED (assigned) inside
 * the window, in every lane alike: one created before it stays hidden until the
 * window is widened. Months are calendar months back from today, so "Last 3
 * months" on 2026-10-01 starts at 2026-07-01. The server applies the window
 * (`window=3m|4m|5m|6m|all`, absent = 3m); the cut-off here mirrors it.
 *
 * Detail pages never go through the window: a direct link always opens.
 */

export const BOARD_WINDOWS = ["3m", "4m", "5m", "6m", "all"] as const;
export type BoardWindow = (typeof BOARD_WINDOWS)[number];
export const DEFAULT_BOARD_WINDOW: BoardWindow = "3m";

/** The select's options. A filter choice, not a business status. */
export const BOARD_WINDOW_LABELS: Record<BoardWindow, string> = {
  "3m": "Last 3 Months",
  "4m": "Last 4 Months",
  "5m": "Last 5 Months",
  "6m": "Last 6 Months",
  all: "All Time",
};

const MONTHS: Record<Exclude<BoardWindow, "all">, number> = { "3m": 3, "4m": 4, "5m": 5, "6m": 6 };

export function isBoardWindow(value: unknown): value is BoardWindow {
  return typeof value === "string" && (BOARD_WINDOWS as readonly string[]).includes(value);
}

/** From the URL: an unknown or missing value falls back rather than breaking the board. */
export function parseBoardWindow(raw: string | null | undefined): BoardWindow {
  return isBoardWindow(raw) ? raw : DEFAULT_BOARD_WINDOW;
}

/** "last 3 months" / "all time" — the tail of the board's summary sentence. */
export function describeBoardWindow(window: BoardWindow): string {
  return BOARD_WINDOW_LABELS[window].toLowerCase();
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/** `today` minus `months` calendar months; a missing day clamps to the month's last. */
export function monthsBefore(today: string, months: number): string {
  const [year, month, day] = today.split("-").map(Number) as [number, number, number];
  const total = year * 12 + (month - 1) - months;
  const targetYear = Math.floor(total / 12);
  const targetMonth = total - targetYear * 12;
  const targetDay = Math.min(day, daysInMonth(targetYear, targetMonth));
  const pad = (value: number, width = 2) => String(value).padStart(width, "0");
  return `${pad(targetYear, 4)}-${pad(targetMonth + 1)}-${pad(targetDay)}`;
}

/** The first day inside the window (YYYY-MM-DD), or null for all time. */
export function windowCreatedFrom(window: BoardWindow, today: string): string | null {
  return window === "all" ? null : monthsBefore(today, MONTHS[window]);
}

/** `createdAt` may be a date or an ISO instant; both compare by UTC day start. */
export function isCreatedInWindow(createdAt: string, createdFrom: string | null): boolean {
  if (createdFrom === null) return true;
  const at = Date.parse(createdAt);
  return Number.isFinite(at) && at >= Date.parse(`${createdFrom}T00:00:00Z`);
}
